import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReportWebVitals } from "next/web-vitals";
import * as z from "zod";
import { hardLoadProofLanding } from "@/components/codesmith/proof/proofLanding";
import { reportWebVital, reportedRoute } from "@/lib/reportWebVital";
const REPORTED_VITALS = ["INP", "LCP", "TTFB", "FCP", "CLS"];
const DOCUMENT_LOAD_VITALS = ["LCP", "FCP", "TTFB"];
const LcpEntry = z.object({
  url: z.string(),
  element: z.object({
    tagName: z.string(),
    dataset: z.object({
      lcpKind: z.string().optional()
    }).optional()
  }).nullable()
});

/**
 * What the LCP entry painted: a video frame, or a recording poster drawn as an
 * img tagged `data-lcp-kind="video"`, is `video`; any other element with a
 * resource URL `image`; an element without one `text`.
 * Mirrored in FrontendMetricsController::LCP_ELEMENTS.
 */
function lcpElementKind(entries: readonly unknown[] | undefined): "image" | "video" | "text" | "other" {
  const parsed = LcpEntry.safeParse(entries?.at(-1));
  if (!parsed.success) return "other";
  const {
    url,
    element
  } = parsed.data;
  if (url === "") return element ? "text" : "other";
  const video = element?.tagName === "VIDEO" || element?.dataset?.lcpKind === "video";
  return video ? "video" : "image";
}
function lcpBreakdown(entries: readonly unknown[] | undefined) {
  const proofSource = hardLoadProofLanding()?.source ?? null;
  return {
    web_vital_element: lcpElementKind(entries),
    ...(proofSource !== null && {
      web_vital_proof_source: proofSource
    })
  };
}

// web-vitals listeners outlive the component that subscribed them, and a soft
// navigation between the (dashboard) and (standalone) App Router groups
// remounts AppProviders. The new mount's subscription re-reads the buffered
// entries and emits the same load under new metric ids, so only the first
// mounted instance per document reports.
let reporterClaimed = false;

/**
 * Reports the page's web vitals to `/frontend/page-metrics`, using the
 * document-start route for load vitals and the current route for lifetime vitals.
 *
 * web-vitals emits INP and CLS when the page is first hidden (tab switch,
 * close) and again on later hides if the value grew; only the first emission
 * per metric id is reported, since a Prometheus histogram cannot retract the
 * earlier sample.
 */
export function useWebVitalsReporting() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const reportedIds = useRef(t0);
  const isReporter = useRef(false);
  const [documentRoute] = useState(_temp);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (reporterClaimed) {
        return;
      }
      reporterClaimed = true;
      isReporter.current = true;
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== documentRoute) {
    t3 = metric => {
      if (!isReporter.current) {
        return;
      }
      if (!REPORTED_VITALS.includes(metric.name)) {
        return;
      }
      if (reportedIds.current.has(metric.id)) {
        return;
      }
      reportedIds.current.add(metric.id);
      const route = DOCUMENT_LOAD_VITALS.includes(metric.name) ? documentRoute : reportedRoute();
      if (!route) {
        return;
      }
      const breakdown = metric.name === "LCP" && metric.navigationType !== "back-forward-cache" ? lcpBreakdown(metric.entries) : undefined;
      reportWebVital(metric.name, metric.value, route, breakdown);
    };
    $[3] = documentRoute;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useReportWebVitals(t3);
}
function _temp() {
  return typeof window !== "undefined" ? reportedRoute() : null;
}